import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Trophy, ArrowRight, LogOut, Settings, User, LayoutDashboard } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { useLanguage } from '../contexts/LanguageContext';
import { branding } from '../config/branding';

interface PublicHeaderProps {
    transparent?: boolean;
}

const PublicHeader: React.FC<PublicHeaderProps> = ({ transparent = false }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, isAuthenticated, logout } = useAuth();
    const { tenant } = useTenant();
    const { t } = useLanguage();

    const goToSignIn = () => navigate('/sign-in');
    const goToSignUp = () => navigate('/sign-up');

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    // Check if we're on the landing page
    const isLandingPage = location.pathname === '/';

    return (
        <nav className="fixed top-0 z-50 w-full">
            <div className="container mx-auto px-4 md:px-6 max-w-7xl pt-4">
                <div className={`flex h-14 items-center justify-between backdrop-blur-xl rounded-2xl shadow-lg shadow-black/5 border px-4 md:px-6 transition-colors ${transparent ? 'bg-white/90 border-gray-200/60' : 'bg-white/95 border-gray-200/60'
                    }`}>
                    {/* Logo */}
                    <Link to={isAuthenticated ? "/dashboard" : "/"} className="flex items-center gap-2.5 group min-w-0 shrink">
                        <div className="relative flex-shrink-0">
                            {tenant?.branding?.logoUrl ? (
                                <img src={tenant.branding.logoUrl} alt={tenant.name || "Logo"} className="h-8 w-auto transition-transform group-hover:scale-110" />
                            ) : (
                                <>
                                    <img src={branding.logos.main} alt="ChessDirector Logo" className="h-8 w-auto transition-transform group-hover:scale-110 group-hover:rotate-3" />
                                    <div className="absolute inset-0 bg-gray-400 opacity-0 group-hover:opacity-20 blur-xl transition-opacity rounded-full" />
                                </>
                            )}
                        </div>
                        <span
                            title={tenant?.name || 'ChessDirector'}
                            className="font-bold text-lg tracking-tight bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent truncate max-w-[45vw] sm:max-w-[16rem]"
                        >
                            {tenant?.name || 'ChessDirector'}
                        </span>
                    </Link>

                    {/* Desktop Nav - Center Links (only on landing page when not authenticated) */}
                    {!isAuthenticated && isLandingPage && (
                        <div className="hidden lg:flex items-center gap-1">
                            <a href="#core" className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all">{t('landing.features', 'Features')}</a>
                            <a href="#developers" className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all">{t('nav.apiDocs', 'API')}</a>
                            <a href="#pricing" className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all">{t('nav.pricing', 'Pricing')}</a>
                            <Link to="/discover" className="px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-1.5">
                                <Trophy className="w-4 h-4" />
                                {t('nav.findEvents', 'Find events')}
                            </Link>
                        </div>
                    )}

                    {/* Desktop Nav - Links for non-landing pages when not authenticated */}
                    {!isAuthenticated && !isLandingPage && (
                        <div className="hidden lg:flex items-center gap-1">
                            <Link to="/" className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all">{t('common.home', 'Home')}</Link>
                            <Link to="/discover" className="px-3 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-1.5">
                                <Trophy className="w-4 h-4" />
                                {t('nav.findEvents', 'Find events')}
                            </Link>
                        </div>
                    )}

                    {/* Desktop Auth Buttons / User Menu */}
                    <div className="hidden lg:flex items-center gap-2">
                        {isAuthenticated ? (
                            <>
                                <Link to="/dashboard" className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-1.5">
                                    <LayoutDashboard className="w-4 h-4" />
                                    {t('nav.dashboard', 'Dashboard')}
                                </Link>
                                <Link to="/settings" className="px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all flex items-center gap-1.5">
                                    <Settings className="w-4 h-4" />
                                    {t('nav.settings', 'Settings')}
                                </Link>
                                <div className="w-px h-5 bg-gray-200 mx-1" />
                                <Link to="/settings" className="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-100 rounded-lg transition-all">
                                    <div className="w-7 h-7 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                                        {user?.avatarUrl ? (
                                            <img src={user.avatarUrl} alt="Profile" className="w-full h-full object-cover" />
                                        ) : (
                                            <User className="w-4 h-4 text-gray-600" />
                                        )}
                                    </div>
                                    <span className="text-sm font-medium text-gray-700">{user?.firstName || user?.username}</span>
                                </Link>
                                <button onClick={handleLogout} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all" title={t('nav.signOut', 'Sign out')}>
                                    <LogOut className="w-4 h-4" />
                                </button>
                            </>
                        ) : (
                            <>
                                <button onClick={goToSignIn} className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all">
                                    {t('common.signIn', 'Log in')}
                                </button>
                                <button onClick={goToSignUp} className="px-4 py-2 text-sm font-bold text-white bg-gray-900 hover:bg-gray-800 rounded-lg transition-all shadow-sm hover:shadow flex items-center gap-1.5">
                                    {t('common.getStarted', 'Get Started')}
                                    <ArrowRight className="w-4 h-4" />
                                </button>
                            </>
                        )}
                    </div>

                    {/* Mobile Menu Button */}
                    <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="lg:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all">
                        {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                    </button>
                </div>
            </div>

            {/* Mobile Drawer */}
            <AnimatePresence>
                {isMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="lg:hidden absolute top-full left-0 right-0 px-4 pt-2"
                    >
                        <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 space-y-1">
                            {!isAuthenticated ? (
                                <>
                                    {isLandingPage ? (
                                        <>
                                            <a href="#core" className="block px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors" onClick={() => setIsMenuOpen(false)}>Features</a>
                                            <a href="#developers" className="block px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors" onClick={() => setIsMenuOpen(false)}>API</a>
                                            <a href="#pricing" className="block px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors" onClick={() => setIsMenuOpen(false)}>Pricing</a>
                                        </>
                                    ) : (
                                        <Link to="/" className="block px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors" onClick={() => setIsMenuOpen(false)}>Home</Link>
                                    )}
                                    <Link to="/discover" className="flex items-center gap-2 px-4 py-3 text-base font-semibold text-gray-900 hover:bg-gray-100 rounded-xl transition-colors" onClick={() => setIsMenuOpen(false)}>
                                        <Trophy className="w-4 h-4" />
                                        Find events
                                    </Link>
                                    <div className="pt-3 mt-3 border-t border-gray-100 grid grid-cols-2 gap-2">
                                        <button onClick={() => { goToSignIn(); setIsMenuOpen(false); }} className="px-4 py-3 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">Log in</button>
                                        <button onClick={() => { goToSignUp(); setIsMenuOpen(false); }} className="px-4 py-3 text-sm font-bold text-white bg-gray-900 hover:bg-gray-800 rounded-xl transition-colors">Sign up</button>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <Link to="/dashboard" className="flex items-center gap-2 px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors" onClick={() => setIsMenuOpen(false)}>
                                        <LayoutDashboard className="w-4 h-4" />
                                        Dashboard
                                    </Link>
                                    <Link to="/settings" className="flex items-center gap-2 px-4 py-3 text-base font-medium text-gray-700 hover:bg-gray-50 rounded-xl transition-colors" onClick={() => setIsMenuOpen(false)}>
                                        <Settings className="w-4 h-4" />
                                        Settings
                                    </Link>
                                    <Link to="/discover" className="flex items-center gap-2 px-4 py-3 text-base font-semibold text-gray-900 hover:bg-gray-100 rounded-xl transition-colors" onClick={() => setIsMenuOpen(false)}>
                                        <Trophy className="w-4 h-4" />
                                        Find events
                                    </Link>
                                    <div className="pt-3 mt-3 border-t border-gray-100">
                                        <button onClick={() => { handleLogout(); setIsMenuOpen(false); }} className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold text-red-600 bg-red-50 hover:bg-red-100 rounded-xl transition-colors">
                                            <LogOut className="w-4 h-4" />
                                            Sign out
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </nav>
    );
};

export default PublicHeader;
